/**
 * AmazonProductNodeView - メモに埋め込む Amazon 商品カードの表示コンポーネント。
 *
 * - 画像 / タイトル / 価格 / ブランド / 星評価 / レビュー数 / 取得日時 を表示
 * - タイトル・画像クリックで Amazon ページを開く
 * - 「再取得」ボタンで最新スナップショットを取得し属性を更新する
 *   (更新中はスピナー表示、失敗時は toast)
 * - カード自体の削除は行を消せば消えるためボタンは持たない
 *
 * データは Tiptap ノード属性に閉じており、DB スキーマ変更は不要 (JSON にそのまま乗る)。
 */

import { format } from 'date-fns'
import { RefreshCw, Star } from 'lucide-react'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'

import { fetchAmazonProduct } from '@/api/services/memo'
import type { NodeViewProps } from '@tiptap/react'
import { NodeViewWrapper } from '@tiptap/react'

import './amazon-product-node.scss'

const formatFetchedAt = (iso: string | null | undefined): string => {
  if (!iso) return '取得日時不明'
  try {
    return `${format(new Date(iso), 'yyyy/MM/dd HH:mm')} 取得`
  } catch {
    return '取得日時不明'
  }
}

export const AmazonProductNodeView = (props: NodeViewProps) => {
  const { node, updateAttributes, editor } = props
  const [isRefreshing, setIsRefreshing] = useState(false)

  const url = String(node.attrs.url ?? '')
  const title = String(node.attrs.title ?? '')
  const image = (node.attrs.image ?? null) as string | null
  const price = (node.attrs.price ?? null) as string | null
  const brand = (node.attrs.brand ?? null) as string | null
  const rating = (node.attrs.rating ?? null) as number | null
  const reviewCount = (node.attrs.reviewCount ?? null) as number | null
  const fetchedAt = String(node.attrs.fetchedAt ?? '')

  const canEdit = editor?.isEditable ?? false

  const handleRefresh = useCallback(async () => {
    if (!url || isRefreshing) return
    setIsRefreshing(true)
    try {
      const snap = await fetchAmazonProduct(url)
      updateAttributes({
        asin: snap.asin,
        url: snap.url,
        title: snap.title,
        image: snap.image,
        price: snap.price,
        brand: snap.brand,
        rating: snap.rating,
        reviewCount: snap.review_count,
        fetchedAt: snap.fetched_at,
      })
      toast.success('Amazon 商品情報を更新しました')
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        '再取得に失敗しました'
      toast.error(message)
    } finally {
      setIsRefreshing(false)
    }
  }, [url, isRefreshing, updateAttributes])

  return (
    <NodeViewWrapper className="amazon-product-node" data-drag-handle="">
      <div className="amazon-product-card">
        <a
          className="amazon-product-card__image"
          href={url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={title}
        >
          {image ? (
            <img src={image} alt={title} loading="lazy" />
          ) : (
            <div className="amazon-product-card__image-fallback">No Image</div>
          )}
        </a>
        <div className="amazon-product-card__body">
          <a
            className="amazon-product-card__title"
            href={url || '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            {title || '(タイトル不明)'}
          </a>
          {brand && <div className="amazon-product-card__brand">{brand}</div>}
          <div className="amazon-product-card__meta">
            {price && <span className="amazon-product-card__price">{price}</span>}
            {rating != null && (
              <span className="amazon-product-card__rating">
                <Star className="size-3.5 fill-yellow-400 stroke-yellow-500" />
                {rating.toFixed(1)}
                {reviewCount != null && (
                  <span className="amazon-product-card__review-count">({reviewCount.toLocaleString()})</span>
                )}
              </span>
            )}
          </div>
          <div className="amazon-product-card__footer">
            <span className="amazon-product-card__fetched-at">{formatFetchedAt(fetchedAt)}</span>
            {canEdit && (
              <button
                type="button"
                className="amazon-product-card__refresh"
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label="Amazon 商品情報を再取得"
                title="再取得"
              >
                <RefreshCw className={`size-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
            )}
          </div>
        </div>
      </div>
    </NodeViewWrapper>
  )
}
